import React from "react";
import { logoLight, logoDark, faviconLight, faviconDark } from "./logoData.js";

/* Tokeniser logo — official lockup + favicon (coin "o" mark), inlined as
   base64 data URIs so it works regardless of where the bundle is mounted.
   - variant: "wordmark" (full lockup) | "mark" (coin only)
   - theme:   "light"    (dark-coloured logo, for use ON LIGHT surfaces) ← default
              "dark"     (light-coloured logo, for use ON DARK surfaces)
*/
const ASPECT = {
  wordmark: 5,
  mark:     23 / 21,           // ≈ 1.095
};

export function Logo({
  variant = "wordmark",
  theme = "light",
  height = 28,
  alt = "Tokeniser",
  brandSrc,
  brandAlt,
  maxWidth: maxWidthProp,
  maxHeight: maxHeightProp,
  style,
  ...rest
}) {
  // brandSrc === undefined means "still loading" — render nothing to avoid flash
  // brandSrc === "" means "loaded, no custom logo" — show default Tokeniser logo
  if (brandSrc === undefined) {
    return null;
  }

  if (brandSrc) {
    const bounded =
      maxWidthProp != null && maxHeightProp != null;
    const maxWidth = maxWidthProp ?? (variant === "mark" ? 48 : 200);
    const maxHeight = maxHeightProp ?? height;
    return (
      <img
        src={brandSrc}
        alt={brandAlt ?? alt ?? "Company logo"}
        data-testid="nav-company-logo"
        style={{
          display: "block",
          ...(bounded
            ? {
                width: "auto",
                height: "auto",
                maxWidth,
                maxHeight,
              }
            : {
                height,
                maxWidth,
                width: "auto",
              }),
          objectFit: "contain",
          ...style,
        }}
        {...rest}
      />
    );
  }

  // The data files are named after the LOGO COLOUR, but `theme` is named
  // after the SURFACE it sits on — so use the dark-coloured asset on light
  // surfaces and the light-coloured asset on dark surfaces.
  // (The favicon assets happen to follow the opposite convention, hence the
  // inverted ternary on the mark branch.)
  const src = variant === "mark"
    ? (theme === "dark" ? faviconDark : faviconLight)
    : (theme === "dark" ? logoLight   : logoDark);
  const aspect = ASPECT[variant] || ASPECT.wordmark;
  return (
    <img
      src={src}
      alt={alt}
      height={height}
      width={Math.round(height * aspect)}
      style={{ display: "block", height, width: "auto", ...style }}
      {...rest}
    />
  );
}

export default Logo;
